import type { OrderStatus } from "@nexus/order-status";
import {
  Button,
  DateRangePicker,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Input,
  Toggle,
  cn,
  type DateRange,
} from "@nexus/ui";
import { useId, useMemo, type ComponentProps, type Ref } from "react";
import { useTranslation } from "react-i18next";

import { OrderLineTypeFilterMenu } from "@/features/marketplace/components/orders-list/OrderLineTypeFilterMenu";
import {
  STATUS_FILTER_OPTION_ITEM_CLASS,
  StatusOptionContent,
} from "@/features/marketplace/components/orders-list/StatusOptionContent";
import { parseYmdToLocalDate, toLocalYmd } from "@/features/marketplace/lib/order-deadline";
import {
  clearPartnerQueueFilters,
  countActivePartnerFilters,
  PARTNER_PERSISTED_STATUS_FILTERS,
  PARTNER_QUEUE_GROUP_BY_OPTIONS,
  PARTNER_QUEUE_SORT_BY_OPTIONS,
  toPartnerFilterFulfillmentStatus,
  type PartnerQueueFilters,
  type PartnerQueueGroupBy,
  type PartnerQueueSortBy,
} from "@/features/marketplace/partner/lib/partner-work-queue";

interface PartnerQueueToolbarProps {
  query: PartnerQueueFilters;
  clients: Array<{ id: string; name: string }>;
  overdueCount: number;
  shownCount: number;
  totalCount: number;
  onChange: (next: PartnerQueueFilters) => void;
  searchInputRef?: Ref<HTMLInputElement>;
}

const GROUP_BY_LABEL_KEY: Record<PartnerQueueGroupBy, string> = {
  none: "marketplace.partner.toolbar.groupNone",
  client: "marketplace.partner.toolbar.groupClient",
  deadline: "marketplace.orders.list.groupDeadline",
};

const SORT_BY_LABEL_KEY: Record<PartnerQueueSortBy, string> = {
  deadline: "marketplace.orders.list.sortDeadline",
  received: "marketplace.partner.columns.received",
  updated: "marketplace.partner.toolbar.sortUpdated",
  client: "marketplace.partner.toolbar.sortClient",
  status: "marketplace.orders.list.sortStatus",
  scripts: "marketplace.orders.list.columns.scripts",
};

function FilterTrigger({
  label,
  active,
  count,
  className,
  ...triggerProps
}: ComponentProps<typeof Button> & {
  label: string;
  active: boolean;
  count?: number;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      iconEnd="chevron-down"
      className={cn(
        "h-8 text-xs",
        active && "border-primary/40 bg-primary-subtle/40 text-primary",
        className,
      )}
      {...triggerProps}
    >
      {label}
      {count ? <span className="tabular-nums">{count}</span> : null}
    </Button>
  );
}

export function PartnerQueueToolbar({
  query,
  clients,
  overdueCount,
  shownCount,
  totalCount,
  onChange,
  searchInputRef,
}: PartnerQueueToolbarProps) {
  const { t } = useTranslation();
  const searchId = useId();
  const activeFilters = countActivePartnerFilters(query);
  const showClientFilter = clients.length > 1;
  const dueRange = useMemo<DateRange | undefined>(() => {
    const from = query.dueFrom ? parseYmdToLocalDate(query.dueFrom) : null;
    const to = query.dueTo ? parseYmdToLocalDate(query.dueTo) : null;
    if (!from && !to) return undefined;
    return { from: from ?? undefined, to: to ?? undefined };
  }, [query.dueFrom, query.dueTo]);

  const toggleStatus = (status: OrderStatus, checked: boolean) => {
    const next = checked
      ? [...query.statuses, status]
      : query.statuses.filter((current) => current !== status);
    onChange({ ...query, statuses: next });
  };

  const toggleClient = (clientId: string, checked: boolean) => {
    const next = checked
      ? [...query.clientIds, clientId]
      : query.clientIds.filter((current) => current !== clientId);
    onChange({ ...query, clientIds: next });
  };

  return (
    <div
      className="flex flex-col gap-2 lg:flex-row lg:items-start"
      role="toolbar"
      aria-label={t("marketplace.partner.toolbar.label", "Filter and sort production work")}
    >
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <div className="w-full sm:w-64">
          <label htmlFor={searchId} className="sr-only">
            {t("marketplace.partner.filters.searchLabel", "Search production work")}
          </label>
          <Input
            id={searchId}
            ref={searchInputRef}
            type="search"
            iconStart="search"
            value={query.search}
            placeholder={t(
              "marketplace.partner.filters.searchPlaceholder",
              "Search order, script or client",
            )}
            onChange={(event) =>
              onChange({ ...query, search: event.target.value })
            }
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <FilterTrigger
              label={t("marketplace.partner.filters.status", "Status")}
              active={query.statuses.length > 0}
              count={query.statuses.length}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-72">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("marketplace.partner.filters.status", "Status")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {PARTNER_PERSISTED_STATUS_FILTERS.map((status) => (
              <DropdownMenuCheckboxItem
                key={status}
                className={STATUS_FILTER_OPTION_ITEM_CLASS}
                checked={query.statuses.includes(status)}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={(checked) =>
                  toggleStatus(status, checked === true)
                }
              >
                <StatusOptionContent
                  status={toPartnerFilterFulfillmentStatus(status)}
                />
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {showClientFilter ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <FilterTrigger
                label={t("marketplace.partner.filters.client", "Client")}
                active={query.clientIds.length > 0}
                count={query.clientIds.length}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-56">
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {t("marketplace.partner.filters.client", "Client")}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {clients.map((client) => (
                <DropdownMenuCheckboxItem
                  key={client.id}
                  className="text-xs"
                  checked={query.clientIds.includes(client.id)}
                  onSelect={(event) => event.preventDefault()}
                  onCheckedChange={(checked) =>
                    toggleClient(client.id, checked === true)
                  }
                >
                  {client.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}

        <OrderLineTypeFilterMenu
          value={query.lineType}
          onChange={(lineType) => onChange({ ...query, lineType })}
          renderTrigger={({ label, isActive }) => (
            <FilterTrigger label={label} active={isActive} />
          )}
        />

        <Toggle
          variant="outline"
          size="sm"
          pressed={query.overdueOnly}
          aria-label={t("marketplace.orders.list.overdueOnly")}
          className={cn(
            "h-8 gap-1.5 px-3 text-xs",
            query.overdueOnly &&
              "border-error/40 bg-error-subtle/60 text-error-subtle-foreground",
          )}
          onPressedChange={(pressed) =>
            onChange({ ...query, overdueOnly: pressed })
          }
        >
          <Icon name="alert-circle" size="sm" color="inherit" aria-hidden />
          {t("marketplace.orders.list.overdueOnly")}
          {overdueCount > 0 ? (
            <span className="tabular-nums text-muted-foreground">
              {overdueCount}
            </span>
          ) : null}
        </Toggle>

        <DateRangePicker
          value={dueRange}
          onChange={(range) =>
            onChange({
              ...query,
              dueFrom: range?.from ? toLocalYmd(range.from) : "",
              dueTo: range?.to ? toLocalYmd(range.to) : "",
            })
          }
          numberOfMonths={2}
          independentMonths={false}
          align="start"
          weekStartsOn={1}
          triggerClassName={cn(
            "h-8 text-xs",
            dueRange && "border-primary/40 bg-primary-subtle/40 text-primary",
          )}
          labels={{
            placeholder: t("marketplace.orders.list.dueFilter"),
            triggerAria: t("marketplace.orders.list.dueRangeAria"),
            clear: t("marketplace.orders.list.clearDueRange"),
            done: t("marketplace.orders.list.applyDueRange"),
            apply: t("marketplace.orders.list.applyDueRange"),
            from: t("marketplace.orders.list.dueFrom"),
            to: t("marketplace.orders.list.dueTo"),
          }}
        />

        {activeFilters > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconStart="x"
            className="h-8 text-xs text-muted-foreground"
            onClick={() => onChange(clearPartnerQueueFilters(query))}
          >
            {t("marketplace.partner.filters.clear", "Clear filters")}
          </Button>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-2 self-end lg:self-auto">
        <span
          className="hidden text-xs tabular-nums text-muted-foreground sm:inline"
          aria-live="polite"
        >
          {shownCount === totalCount
            ? t("marketplace.orders.list.resultCount", { count: totalCount })
            : t("marketplace.orders.list.resultCountFiltered", {
                shown: shownCount,
                total: totalCount,
              })}
        </span>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconStart="rows-3"
              iconEnd="chevron-down"
              className={cn(
                "h-8 text-xs",
                query.groupBy !== "none" && "text-primary",
              )}
              aria-label={t("marketplace.orders.list.groupByLabel")}
            >
              {query.groupBy === "none"
                ? t("marketplace.orders.list.groupBy")
                : t(GROUP_BY_LABEL_KEY[query.groupBy])}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-44">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("marketplace.orders.list.groupByLabel")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={query.groupBy}
              onValueChange={(value) =>
                onChange({ ...query, groupBy: value as PartnerQueueGroupBy })
              }
            >
              {PARTNER_QUEUE_GROUP_BY_OPTIONS.map((option) => (
                <DropdownMenuRadioItem
                  key={option}
                  value={option}
                  className="text-xs"
                >
                  {t(GROUP_BY_LABEL_KEY[option])}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconStart="sliders-horizontal"
              iconEnd="chevron-down"
              className="h-8 text-xs"
              aria-label={t("marketplace.orders.list.sortByLabel")}
            >
              {t(SORT_BY_LABEL_KEY[query.sortBy])}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-44">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("marketplace.orders.list.sortByLabel")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={query.sortBy}
              onValueChange={(value) => {
                const sortBy = value as PartnerQueueSortBy;
                onChange({
                  ...query,
                  sortBy,
                  sortDirection:
                    sortBy === "updated" || sortBy === "received"
                      ? "desc"
                      : "asc",
                });
              }}
            >
              {PARTNER_QUEUE_SORT_BY_OPTIONS.map((option) => (
                <DropdownMenuRadioItem
                  key={option}
                  value={option}
                  className="text-xs"
                >
                  {t(SORT_BY_LABEL_KEY[option])}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t("marketplace.orders.list.density.label")}
            >
              <Icon
                name={query.density === "compact" ? "rows-4" : "rows-2"}
                size="sm"
                color="inherit"
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-40">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("marketplace.orders.list.density.label")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={query.density}
              onValueChange={(value) =>
                onChange({
                  ...query,
                  density: value as PartnerQueueFilters["density"],
                })
              }
            >
              <DropdownMenuRadioItem value="compact" className="text-xs">
                {t("marketplace.orders.list.density.compact")}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="comfortable" className="text-xs">
                {t("marketplace.orders.list.density.comfortable")}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
